import React, { useMemo, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import TicketTable from "../components/TicketTable";
import { useAppSelector, useSupervisorHook } from "../hooks";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import GlobalLoadingOverlay from "../components/GlobalLoadingOverlay";
import { useUserMsp } from "../hooks/useUserMsp";
import { useIntegration } from "../hooks/useIntegration";
import { isNinjaOne } from "../utils/mspUtils";
import { defaultOpenStatusSelection, isOpenTicketStatus, matchesSelectedStatus } from "../utils/ticketStatusFilter";
import { usePsaTicketStatusOptions } from "../hooks/usePsaTicketStatusOptions";
import { OPEN_TICKETS_LABEL } from "../navigation/workScopes";
import { Ticket } from "../types/supervisorType";
import { Customer } from "../types/integration";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import { isServiceDeskInboxFlagEnabled } from "../utils/serviceDeskInbox";

function normalizePsaCustomerId(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).toLowerCase().trim();
}

const Supervisor: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const flags = useFeatureFlag();
  const serviceDeskInboxEnabled = isServiceDeskInboxFlagEnabled(
    flags as Record<string, unknown>
  );
  const { userMspInfo } = useUserMsp();
  const { customers, getCustomers } = useIntegration();
  const psaStatusOptions = usePsaTicketStatusOptions(userMspInfo?.msp_type);
  const [appliedStatus, setAppliedStatus] = useState<string[] | undefined>(undefined);
  
  // Fetch customers on mount
  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  const ninjaoneBoardList = useAppSelector(
    (state: any) => state.supervisor.ninjaoneBoardList
  );

  // Find the 'All tickets' board for NinjaOne
  const allTicketsBoard = useMemo(() => {
    if (
      userMspInfo?.msp_type &&
      isNinjaOne(userMspInfo.msp_type) &&
      Array.isArray(ninjaoneBoardList)
    ) {
      return ninjaoneBoardList.find(
        (b: any) => b.name?.toLowerCase() === "all tickets"
      );
    }
    return null;
  }, [userMspInfo?.msp_type, ninjaoneBoardList]);

  // Use correct initial filters for NinjaOne 'All tickets' board
  const initialFilters = useMemo(() => {
    if (
      userMspInfo?.msp_type &&
      isNinjaOne(userMspInfo.msp_type) &&
      allTicketsBoard
    ) {
      return { groupName: allTicketsBoard.name, boardId: allTicketsBoard.id };
    }
    return {};
  }, [userMspInfo?.msp_type, allTicketsBoard]);

  const {
    isLoading,
    tickets,
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    filters,
    setFilters,
    sort,
    setSort,
  } = useSupervisorHook(initialFilters);

  // Always ensure boardId is present in filters for NinjaOne
  useEffect(() => {
    if (
      userMspInfo?.msp_type &&
      isNinjaOne(userMspInfo.msp_type) &&
      allTicketsBoard &&
      (!filters.boardId || filters.boardId !== allTicketsBoard.id)
    ) {
      setFilters((prev: { groupName?: string; status?: string[]; customer?: string[]; boardId?: number }) => ({
        ...prev,
        groupName: allTicketsBoard.name,
        boardId: allTicketsBoard.id,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMspInfo?.msp_type, allTicketsBoard, setFilters]);

  const navigateToTicketDetail = (ticketId: string | number) => {
    navigate(`/supervisor/tickets/${ticketId}`);
  };

  const buildTicketDetailPath = (ticketId: string) => {
    const returnTo = `${location.pathname}${location.search}`;
    const detailSearch = new URLSearchParams();
    detailSearch.set("returnTo", returnTo);
    return `/supervisor/tickets/${ticketId}?${detailSearch.toString()}`;
  };

  const handleSortChange = (
    newSort: { attribute: string; order: "ASC" | "DESC" }[]
  ) => {
    setSort(newSort);
  };

  const handleRefreshTickets = () => {
    setFilters((prev: { groupName?: string; status?: string[]; customer?: string[]; boardId?: number; refreshToken?: string }) => ({
      ...prev,
      refreshToken: String(Date.now()),
    }));
  };

  // Get MSP customers and their PSA IDs
  const mspPsaCustomerIds = useMemo(() => {
    const allCustomers = (customers?.rows || customers || []) as Customer[];
    const mspCustomers = allCustomers.filter(
      (c: Customer) =>
        String((c as any).msp_id) === String(userMspInfo?.msp_id) &&
        c.name?.toLowerCase() !== "root" &&
        !isMspOrgCustomer(c) &&
        normalizePsaCustomerId(c.psa_customer_id)
    );
    
    // Create set of PSA customer IDs (normalized as strings)
    const psaIds = new Set<string>();
    mspCustomers.forEach((c: Customer) => {
      const psaCustomerId = normalizePsaCustomerId(c.psa_customer_id);
      if (psaCustomerId) {
        psaIds.add(psaCustomerId);
      }
    });
    
    return psaIds;
  }, [customers, userMspInfo?.msp_id]);

  // Customer scope, then the status checkboxes. The default selection is open statuses.
  const customerScopedTickets = useMemo(() => {
    if (!tickets || tickets.length === 0 || mspPsaCustomerIds.size === 0) {
      return tickets || [];
    }
    return tickets.filter((ticket: Ticket) => {
      const client = ticket.client;
      let accountId: string | null = null;
      if (typeof client === "string" || typeof client === "number") {
        accountId = normalizePsaCustomerId(client);
      } else if (client && typeof client === "object") {
        const clientObj = client as any;
        accountId = normalizePsaCustomerId(clientObj.accountId) || null;
      }
      return accountId ? mspPsaCustomerIds.has(accountId) : false;
    });
  }, [tickets, mspPsaCustomerIds]);

  const statusOptions = useMemo(() => {
    const seen = customerScopedTickets
      .map((ticket: Ticket) => String(ticket?.status || "").trim())
      .filter(Boolean);
    return Array.from(new Set([...psaStatusOptions, ...seen]));
  }, [customerScopedTickets, psaStatusOptions]);

  // Statuses constrain the PSA query before the latest-ticket window is chosen.
  // Filtering only the downloaded window can hide recent completed/open work.
  useEffect(() => {
    const status = appliedStatus ?? defaultOpenStatusSelection(statusOptions, userMspInfo?.msp_type);
    setFilters(prev => JSON.stringify([...(prev.status || [])].sort()) === JSON.stringify([...status].sort()) ? prev : { ...prev, status });
  }, [appliedStatus, statusOptions, userMspInfo?.msp_type, setFilters]);

  const filteredTickets = useMemo(
    () =>
      customerScopedTickets.filter((ticket: Ticket) =>
        appliedStatus === undefined
          ? isOpenTicketStatus(ticket.status, userMspInfo?.msp_type)
          : matchesSelectedStatus(ticket.status, appliedStatus, {
              mspType: userMspInfo?.msp_type,
              strictMatch: false,
            })
      ),
    [customerScopedTickets, appliedStatus, userMspInfo?.msp_type]
  );

  const totalCount = filteredTickets.length;
  const currentPageTickets = useMemo(
    () =>
      filteredTickets.slice((page - 1) * pageSize, page * pageSize),
    [filteredTickets, page, pageSize]
  );

  if (isLoading) {
    return <GlobalLoadingOverlay />;
  }

  return (
    <Box
      sx={{
        p: 1,
        boxSizing: "border-box",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
          <TicketTable
            title={OPEN_TICKETS_LABEL}
            data={currentPageTickets}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRowClick={serviceDeskInboxEnabled ? undefined : navigateToTicketDetail}
            openTicketDetailInPopup={serviceDeskInboxEnabled}
            buildTicketDetailPath={
              serviceDeskInboxEnabled ? buildTicketDetailPath : undefined
            }
            filters={{ ...filters, status: appliedStatus }}
            onFilterChange={(next) => {
              setAppliedStatus(next.status);
              setFilters((prev: { groupName?: string; status?: string[]; customer?: string[]; boardId?: number; refreshToken?: string }) => ({
                ...prev,
                customer: next.customer,
              }));
            }}
            statusOptions={statusOptions}
            sort={sort}
            onSortChange={handleSortChange}
            showControls={true}
            mspType={userMspInfo?.msp_type}
            onRefresh={handleRefreshTickets}
            showClosedSection={false}
          />
    </Box>
  );
};

export default Supervisor;
